import React, { useEffect, useMemo, useState } from "react";
import { Pagination } from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { getBenefitsLogSagaAction } from "../../../../../redux/saga/partner-program/benefits-log/saga-actions";
import { getPartnerProgramBenefitsSagaAction } from "../../../../../redux/saga/partner-program/benefits/saga-actions";
import { benefitsLogSliceAction } from "../../../../../redux/slices/partner-program/benefits-log";
import { sortLogsLatestFirst } from "../../../../../redux/slices/partner-program/benefits-log/sortLogs";
import {
  mapActivityLogToHistoryRow,
  statusClassName,
} from "../../../../hid-admin/dashboard/partner-organizations/manage-partner/partner-program/benefits-log/benefitsLogUtils";
import "../../../../hid-admin/dashboard/partner-organizations/manage-partner/partner-program/benefits-log/BenefitsLog.scss";
import "./BenefitsLog.scss";

const DELIVERY_BENEFITS_TYPE = "delivery";

/** Partner view is display-only, so View and Edit currently render the same. */
const BenefitsLog: React.FC<{ readOnly?: boolean }> = ({ readOnly: _readOnly = false }) => {
  const dispatch = useAppDispatch();
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const {
    logs,
    pagination,
    status: { loading: logsLoading },
  } = useAppSelector((state) => state.partner_program_benefits_log);
  const {
    categories,
    status: { benefitsLoading },
  } = useAppSelector((state) => state.partner_program_benefits);

  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [benefitsFetchStarted, setBenefitsFetchStarted] = useState(false);

  useEffect(() => {
    dispatch(getPartnerProgramBenefitsSagaAction());
  }, [dispatch]);

  useEffect(() => {
    if (benefitsLoading) setBenefitsFetchStarted(true);
  }, [benefitsLoading]);

  const benefitsReady = benefitsFetchStarted && !benefitsLoading;

  const deliveryBenefitIds = useMemo(
    () =>
      categories
        .flatMap((category) => category.benefits)
        .filter(
          (benefit) =>
            benefit.apiId &&
            (benefit.benefitsType || "").trim().toLowerCase() ===
              DELIVERY_BENEFITS_TYPE
        )
        .map((benefit) => String(benefit.apiId)),
    [categories]
  );
  const deliveryBenefitIdsKey = deliveryBenefitIds.join(",");

  useEffect(() => {
    if (!benefitsReady) return;
    // An empty benefit_ids filter would return every log, so skip the call.
    if (!partnerDetails?.ID || !deliveryBenefitIdsKey) {
      dispatch(benefitsLogSliceAction.clearBenefitsLogList());
      return;
    }
    dispatch(
      getBenefitsLogSagaAction({
        partnerId: partnerDetails.ID,
        query: {
          page: currentPageNumber,
          limit: pageSize,
          benefitIds: deliveryBenefitIdsKey.split(","),
        },
      })
    );
  }, [
    dispatch,
    benefitsReady,
    partnerDetails?.ID,
    deliveryBenefitIdsKey,
    currentPageNumber,
    pageSize,
  ]);

  const isLoading = !benefitsReady || logsLoading;

  const pagedRows = useMemo(
    () => sortLogsLatestFirst(logs).map(mapActivityLogToHistoryRow),
    [logs]
  );

  const totalRecords = pagination.totalRecords;

  return (
    <div className="benefits-log partner-benefits-log">
      <section className="bl-card">
        <div className="bl-card-header">
          <h2>Log History</h2>
          <p>List of previous Log</p>
        </div>

        <div className="bl-history-table-wrap">
          <div className="bl-history-table-scroll">
            <table className="bl-history-table">
              <thead>
                <tr>
                  <th className="col-benefit">Benefits</th>
                  <th className="col-date">Planned Date</th>
                  <th className="col-date">Delivered Date</th>
                  <th className="col-status">Status</th>
                  <th className="col-owner">HID Owner</th>
                  <th className="col-owner">Partner Owner</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="bl-history-empty">
                      Loading log history...
                    </td>
                  </tr>
                ) : pagedRows.length > 0 ? (
                  pagedRows.map((row) => (
                    <tr key={row.id}>
                      <td className="col-benefit">{row.benefit}</td>
                      <td className="col-date">{row.plannedDate}</td>
                      <td className="col-date">{row.deliveredDate}</td>
                      <td className="col-status">
                        <span
                          className={`bl-status-badge ${statusClassName(
                            row.status
                          )}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="col-owner">{row.hidOwner}</td>
                      <td className="col-owner">{row.partnerOwner}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="bl-history-empty">
                      No data available for display.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPageNumber={currentPageNumber}
            totalRecords={totalRecords}
            pageSize={pageSize}
            border={true}
            onPageNumberClick={(clickedPageNumber: number | null) => {
              if (clickedPageNumber) setCurrentPageNumber(clickedPageNumber);
            }}
            onPageSizesClick={(_e: unknown, value: string) => {
              setPageSize(Number(value));
              setCurrentPageNumber(1);
            }}
            pageSizes={[
              { label: "20", value: "20" },
              { label: "50", value: "50" },
              { label: "100", value: "100" },
            ]}
          />
        </div>
      </section>
    </div>
  );
};

export default BenefitsLog;
